import React, { useState, useRef, useEffect } from "react";
import {
  makeStyles,
  Divider,
  Grid,
  Typography,
  TextField,
  Menu,
  MenuItem,
  Button,
  IconButton,
} from "@material-ui/core";
import { DateTime } from "luxon";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import parse from "html-react-parser";

import ListPageWrapper from "components/ListPageWrapper";
import AmoTableWithFilters from "components/table/AmoTableWithFilters";

import { useConditionalQueryParams } from "hooks/conditionalQueryParamsHook";
import { useIsMounted } from "hooks/useIsMounted";

import { useYearContext } from "contexts/YearContext";
import { useLastAuditedYearContext } from "contexts/LastAuditedYearContext";
import { useSnackbar } from "contexts/SnackbarContext";
import colors from "constants/colors";
import { snackbarTypes } from "constants/snackbar";
import { reconciliationTypes } from "constants/reconciliationTypes";
import {
  displayTypes,
  tableDefaultActions,
  tableDefaultActionsConfigs,
} from "constants/amoTableConstants";

import { exportReconciliationToExcel, exportToExcel } from "utils/export";
import { downloadFile } from "utils/pdf";

import * as filesService from "api/services/fileService";
import { useReconciliationReviews } from "./reconciliationReviewsHooks";
import { errorMessages } from "constants/errorMessages";
import ReconciliationEntry from "components/amo/reconciliationReviews/ReconciliationEntry";
import ReconciliationTitleRow from "components/amo/reconciliationReviews/ReconciliationTitleRow";
import ReconciliationTotalRow from "components/amo/reconciliationReviews/ReconciliationTotalRow";
import { ReactComponent as HelpIcon } from "img/icons/help_low_icon.svg";
import { ReactComponent as DownloadIcon } from "img/icons/download_circle_thin_icon.svg";
import ReconciliationHelpModal from "components/amo/ReconciliationHelpModal";
import ReconciliationCloseModal from "components/amo/ReconciliationCloseModal";
import ReconciliationUpdateModal from "components/amo/ReconciliationUpdateModal";
import { useWrapApi } from "hooks/wrapApiHook";
import * as reviewService from "api/services/reviewsService";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    width: "3rem",
    height: "3rem",
    margin: "0 0",
    padding: "0 0",
  },
  dividerContainer: {
    paddingBottom: "0.688rem !important",
  },
  buttonsBar: {
    paddingBottom: "1.438rem !important",
  },
  outlinedIconButtonDisabled: {
    border: `0.063rem solid rgba(0, 0, 0, 0.12)`,
    color: "#939393",
    backgroundColor: "white !important",
  },
  loadingProgress: {
    color: colors.grey.light,
  },
  icon: {
    width: "3rem",
    height: "3rem",
  },
  iconButtonYear: {
    height: "3rem",
    border: `0.125rem solid ${theme.palette.primary.main}`,
    "&:hover": {
      borderWidth: "0.125rem",
    },
  },
  header: {
    color: colors.green.dark,
  },
  firstColumnCell: {
    paddingLeft: "1.25rem",
    minWidth: "25rem",
  },
  cell: {
    minWidth: "12.5rem",
  },
  lastColumnCell: {
    minWidth: "16rem",
  },
  middleColumnSpacing: {
    marginLeft: "1.5rem",
  },
  firstRow: {
    paddingLeft: "1.25rem",
    paddingTop: "0.5rem",
  },
  rowDivider: {
    marginTop: "0.8rem",
  },
  underlined: {
    textDecoration: "underline",
  },
  margins: {
    marginRight: "0.5rem",
    marginLeft: "0.5rem",
  },
  verticalMargins: {
    marginTop: 16,
  },
  divider: {
    background: colors.green.dark,
    marginTop: "1rem",
    marginBottom: "-1rem",
  },
}));

/**
 * The Reconciliation Review List Page.
 *
 * @returns {React.Component} Reconciliation Review List Page component.
 */
const ReconciliationReviewListPage = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const { contextYear } = useYearContext();
  const { refetchLastAuditedYear } = useLastAuditedYearContext();
  const history = useHistory();
  const location = useLocation();

  const mounted = useIsMounted();

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedItem, setFocusedItem] = useState({});
  const rowRefs = useRef({});
  const filteredData = useRef([]);

  const [selectedType, setSelectedType] = useConditionalQueryParams(
    "type",
    "string",
    reconciliationTypes.current,
    true
  );

  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const ellipsisOnClick = (event) => setEllipsisAnchorEl(event.currentTarget);
  const ellipsisOnClose = () => setEllipsisAnchorEl(null);

  const postReconciliation = useWrapApi(reviewService.postReconciliation);

  const updateReconciliation = useWrapApi(reviewService.putReconciliation);

  const {
    data: reconciliations,
    isFetching: reconciliationsFetching,
    isFetched,
    refetch,
  } = useReconciliationReviews(contextYear - 1, selectedType);

  const getTableColumns = () => [
    {
      fieldKey: "municipalityName",
      label: "Municipality",
      minWidth: "11rem",
    },
    {
      fieldKey: "statusReconciliation",
      label: "Reason",
      minWidth: "11rem",
    },
    {
      fieldKey: "reason",
      label: "Description",
      minWidth: "11rem",
    },
    {
      fieldKey: "authority",
      label: "Authority",
      minWidth: "11rem",
    },
    {
      fieldKey: "year",
      label: "Reporting Year",
      minWidth: "11rem",
      horizontalAlign: "right",
    },
    {
      fieldKey: "amount",
      label: "Amount",
      minWidth: "11rem",
      displayType: displayTypes.currency,
      isRounded: false,
    },

    ...(reconciliations?.questions?.map(({ id, text }) => ({
      fieldKey: `${id}`,
      label: parse(text),
      minWidth: "12.5rem",
      maxWidth: "15rem",
      customRender: ({ [id]: value }) => (
        <Typography variant="body1" noWrap title={value}>
          {value}
        </Typography>
      ),
      customExport: ({ [id]: value }) => value,
      noHeaderWrap: true,
      isCustom: true,
    })) ?? []),
  ];

  const setFiltersForChanges = () => {
    const queryParam = qs.parse(location.search);
    if (queryParam.filters !== undefined) {
      const newQuery = {
        type: reconciliationTypes.current,
        order: "asc",
        orderBy: "id",
        page: 1,
        filter_name: queryParam.filter_name,
      };

      history.push({
        pathname: location.pathname,
        search: qs.stringify(newQuery),
      });
    }
  };

  useEffect(() => {
    setFiltersForChanges();
  }, [contextYear]);

  const onSaveReconciliationCreate = async () => {
    try {
      const response = await postReconciliation.call(contextYear - 1);
      if (response.error) {
        return;
      }

      showSnackbar(
        `${contextYear - 1} was successfully closed`,
        snackbarTypes.success
      );
      refetch();
      refetchLastAuditedYear();
    } catch (error) {
      showSnackbar(
        error.message || "Failed to close year",
        snackbarTypes.error
      );
    } finally {
      setShowCloseModal(false);
    }
  };

  const onSaveReconciliationUpdate = async () => {
    try {
      const response = await updateReconciliation.call(contextYear - 1);
      if (response.error) {
        return;
      }

      showSnackbar(
        `${contextYear - 1} was successfully updated`,
        snackbarTypes.success
      );
      refetch();
      refetchLastAuditedYear();
    } catch (error) {
      showSnackbar(
        error.message || "Failed to update year",
        snackbarTypes.error
      );
    } finally {
      setShowUpdateModal(false);
    }
  };

  const downloadExcel = () => {
    try {
      if (selectedType === reconciliationTypes.changes) {
        exportToExcel(
          filteredData.current,
          getTableColumns(),
          `Reconciliation Review - ${contextYear} - ${selectedType}`,
          { sheetName: "Reviews" }
        );
      } else {
        exportReconciliationToExcel(
          reconciliations,
          contextYear - 1,
          selectedType
        );
      }
    } catch (error) {
      console.error(error);

      showSnackbar(errorMessages.creatingSpreadsheet, snackbarTypes.error);
    }
  };

  const downloadReconciliations = async () => {
    setIsLoading(true);

    try {
      const response = await filesService.getReconciliationsURL(
        contextYear - 1
      );

      if (!mounted.current) {
        return;
      }

      const dateNow = DateTime.now().toFormat("yyyyMMddHHmmss");
      const fileName = `Reconciliation Review - ${contextYear} - ${selectedType} - Reconciliation_files_${dateNow}.zip`;
      downloadFile(response.data, fileName);
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      if (error?.response?.status === 404) {
        showSnackbar("No Reconciliation file found", snackbarTypes.info);
      } else {
        showSnackbar(
          "An error occurred while downloading the Reconciliation files",
          snackbarTypes.error
        );
      }
    } finally {
      if (mounted.current) {
        setIsLoading(false);
      }
    }
  };

  const handleItemClick = (item) => {
    if (selectedType === "Changes") {
      history.push(
        `${`/reviews/reconciliations/${item.id}`}${location.search}`
      );
    }
  };

  const typeDropdown = (
    <TextField
      variant="outlined"
      value={selectedType}
      onChange={(event) => {
        setSelectedType(event.target.value);
        setFiltersForChanges();
      }}
      select
    >
      <MenuItem value={reconciliationTypes.current}>
        {reconciliationTypes.current}
      </MenuItem>
      <MenuItem value={reconciliationTypes.cumulative}>
        {reconciliationTypes.cumulative}
      </MenuItem>
      <MenuItem value={reconciliationTypes.changes}>
        {reconciliationTypes.changes}
      </MenuItem>
    </TextField>
  );

  const closeYearButton = (
    <Button
      aria-label="tableActionSearch"
      color="primary"
      size="large"
      variant="outlined"
      className={classes.iconButtonYear}
      onClick={() => {
        reconciliations?.isAudited
          ? setShowUpdateModal(true)
          : setShowCloseModal(true);
      }}
      disabled={
        reconciliationsFetching ||
        (reconciliations?.isAudited && !reconciliations?.hasChanges)
      }
    >
      {reconciliations?.isAudited
        ? `Update ${contextYear - 1}`
        : `Close ${contextYear - 1}`}
    </Button>
  );
  const helpButton = (
    <IconButton
      id="help-button"
      data-testid="helpButtonTest"
      aria-label="help"
      onClick={() => {
        setShowHelpModal(true);
      }}
      className={classes.iconButton}
      color="primary"
    >
      <HelpIcon className={classes.icon} />
    </IconButton>
  );

  const tableActions = {
    titleComponent: typeDropdown,
    actions: ["closeYear", tableDefaultActions.export, "help"],
    actionsCustomConfigs: {
      [tableDefaultActions.export]: {
        onClick: ellipsisOnClick,
        disabled: isLoading || !reconciliations?.isAudited,
      },
    },
    customActionsComponents: {
      closeYear: closeYearButton,
      help: helpButton,
    },
  };

  const renderAction = (action) => {
    let actionComponent;
    const actionConfig = {
      ...(tableDefaultActionsConfigs[action] ?? {}),
      ...(tableActions.actionsCustomConfigs[action] ?? {}),
    };
    switch (action) {
      case tableDefaultActions.export:
        actionComponent = (
          <IconButton
            id="reconciliation-export-button"
            data-testid="reconciliation-exportButtonTest"
            aria-label="reconciliation-export"
            onClick={actionConfig?.onClick}
            className={classes.iconButton}
            disabled={actionConfig?.disabled || reconciliationsFetching}
            color="primary"
          >
            <DownloadIcon
              className={classes.icon}
              style={{
                stroke:
                  !actionConfig?.disabled && !reconciliationsFetching
                    ? colors.green.main
                    : colors.grey.main,
              }}
              fill={
                !actionConfig?.disabled && !reconciliationsFetching
                  ? colors.green.main
                  : colors.grey.main
              }
            />
          </IconButton>
        );
        break;
      default:
        actionComponent = tableActions.customActionsComponents[action] ?? null;
        break;
    }
    return (
      !!actionComponent && (
        <Grid item key={`reconciliation-${action}`}>
          {actionComponent}
        </Grid>
      )
    );
  };

  return (
    <ListPageWrapper>
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <Typography variant="h1">Reconciliation</Typography>
        </Grid>
        <Grid item className={classes.dividerContainer}>
          <Divider />
        </Grid>
        {selectedType !== reconciliationTypes.changes && (
          <>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item>{tableActions.titleComponent}</Grid>
              <Grid item xs>
                <Grid
                  id="recon"
                  container
                  justifyContent="flex-end"
                  spacing={2}
                  wrap="nowrap"
                >
                  {tableActions.actions.map((action) => renderAction(action))}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        {isFetched &&
          !reconciliations?.isAudited &&
          selectedType !== reconciliationTypes.changes && (
            <Grid item>
              <Typography>
                {`${contextYear - 1}’s audit has not begun.`}
              </Typography>
            </Grid>
          )}
        {!isFetched && (
          <Grid item>
            <Typography>{`${contextYear - 1}’s loading.`}</Typography>
          </Grid>
        )}
        {isFetched &&
          selectedType === reconciliationTypes.changes &&
          !reconciliations?.length && (
            <AmoTableWithFilters
              items={reconciliations?.data || []}
              columns={getTableColumns()}
              defaultOrderBy="municipality"
              defaultOrder="asc"
              enableItemClick
              onItemClick={handleItemClick}
              onFilteredDataChanged={(value) => {
                filteredData.current = value;
              }}
              debouncingTime={450}
              onScroll={(item) => {
                rowRefs.current?.[item.reconciliationId]?.focus();
                setFocusedItem(item.reconciliationId);
              }}
              focusedItem={focusedItem}
              actionsProps={tableActions}
              isLoading={reconciliationsFetching}
              uniqueKeyGenerationFields={["reconciliationId"]}
            />
          )}
        {isFetched &&
          reconciliations?.isAudited &&
          (selectedType === reconciliationTypes.cumulative ||
            selectedType === reconciliationTypes.current) && (
            <Grid
              container
              direction="column"
              className={classes.margins}
              style={{
                minWidth: `${25 + 12.5 * 4 + 16}rem`,
              }}
              wrap="nowrap"
            >
              <Grid
                container
                direction="row"
                wrap="nowrap"
                className={classes.firstRow}
                align="right"
              >
                <Grid item className={classes.lastColumnCell} align="left">
                  <Typography variant="h5" className={classes.header}>
                    {selectedType === reconciliationTypes.cumulative
                      ? `Cumulative (2005 - ${contextYear - 1})`
                      : `${contextYear - 1} audit year`}
                  </Typography>
                </Grid>
                <Grid item className={classes.firstColumnCell} />

                {["Audited", "Current", "Changes", "Variance"].map(
                  (columnName) => (
                    <Grid item className={classes.cell} key={columnName}>
                      <Typography variant="h5" className={classes.header}>
                        {columnName}
                      </Typography>
                    </Grid>
                  )
                )}

                <Grid
                  className={classes.middleColumnSpacing}
                  style={{ marginRight: 4 }}
                />
              </Grid>
              <Divider className={classes.divider} />
              <ReconciliationTitleRow
                displayName="Opening balance"
                classes={classes}
                reconciliationDataFieldName="openBalance"
                reconciliationData={reconciliations}
                underlined={false}
              />

              <ReconciliationTitleRow
                displayName="Revenues"
                classes={classes}
              />
              <ReconciliationEntry
                displayName="Allocations"
                reconciliationDataFieldName="payments"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative={false}
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Interest earnings"
                reconciliationDataFieldName="interestEarned"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative={false}
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Disposals"
                reconciliationDataFieldName="proceedsFromDisposal"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative={false}
                enableVarianceHighlight
              />

              <ReconciliationTotalRow
                displayName="Net Revenues"
                reconciliationDataFieldName="netRevenues"
                reconciliationData={reconciliations}
                classes={classes}
              />

              <ReconciliationTitleRow
                displayName="Transfers"
                classes={classes}
              />
              <ReconciliationEntry
                displayName="Transfers out"
                reconciliationDataFieldName="transferredOut"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Transfers in"
                reconciliationDataFieldName="transferredIn"
                reconciliationData={reconciliations}
                classes={classes}
                enableVarianceHighlight
              />

              <ReconciliationTotalRow
                displayName="Net Transfers"
                reconciliationDataFieldName="netTransfers"
                reconciliationData={reconciliations}
                classes={classes}
              />

              <ReconciliationTitleRow
                displayName="Expenditures"
                classes={classes}
              />
              <ReconciliationEntry
                displayName="Broadband Connectivity"
                reconciliationDataFieldName="broadband"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Brownfield Redevelopment"
                reconciliationDataFieldName="brownfield"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Capacity Building"
                reconciliationDataFieldName="capacityBldg"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Community Energy Systems"
                reconciliationDataFieldName="commEnergy"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Culture"
                reconciliationDataFieldName="culture"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Drinking Water"
                reconciliationDataFieldName="water"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Fire Stations"
                reconciliationDataFieldName="fireStation"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                dontShowBeforeYear={2021}
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Local Roads and Bridges"
                reconciliationDataFieldName="roadsBridges"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Public Transit"
                reconciliationDataFieldName="transit"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Recreation"
                reconciliationDataFieldName="recreation"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Resilience"
                reconciliationDataFieldName="disaster"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Regional and Local Airports"
                reconciliationDataFieldName="airport"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Short-line Rail"
                reconciliationDataFieldName="shortline"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Short-sea Shipping"
                reconciliationDataFieldName="shortsea"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Solid Waste"
                reconciliationDataFieldName="solidWaste"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Sports"
                reconciliationDataFieldName="sport"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Tourism"
                reconciliationDataFieldName="tourism"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />
              <ReconciliationEntry
                displayName="Wastewater"
                reconciliationDataFieldName="wastewater"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
                enableVarianceHighlight
              />

              <ReconciliationTotalRow
                displayName="Net Expenditures"
                reconciliationDataFieldName="netExpenditures"
                reconciliationData={reconciliations}
                classes={classes}
                isNegative
              />

              <ReconciliationTitleRow
                displayName="Closing balance"
                classes={classes}
                reconciliationDataFieldName="closeBalance"
                reconciliationData={reconciliations}
                underlined={false}
              />
            </Grid>
          )}
      </Grid>
      <Menu
        anchorEl={ellipsisAnchorEl}
        open={!!ellipsisAnchorEl}
        onClose={ellipsisOnClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem
          onClick={() => {
            ellipsisOnClose();
            downloadExcel();
          }}
        >
          <Typography variant="body1">Download data</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            ellipsisOnClose();
            downloadReconciliations();
          }}
        >
          <Typography variant="body1">Download files</Typography>
        </MenuItem>
      </Menu>
      <ReconciliationHelpModal
        showModal={showHelpModal}
        closeModal={() => {
          setShowHelpModal(false);
        }}
        year={contextYear - 1}
      />
      <ReconciliationCloseModal
        showModal={showCloseModal}
        closeModal={() => {
          setShowCloseModal(false);
        }}
        confirmButton={onSaveReconciliationCreate}
        year={contextYear - 1}
      />
      <ReconciliationUpdateModal
        showModal={showUpdateModal}
        closeModal={() => {
          setShowUpdateModal(false);
        }}
        confirmButton={onSaveReconciliationUpdate}
        year={contextYear - 1}
      />
    </ListPageWrapper>
  );
};

export default ReconciliationReviewListPage;
